import { useSyncExternalStore } from 'react';
import { api } from '@/lib/api';
import { authStore } from '@/lib/auth/auth-store';
import { isOnline } from '@/lib/hooks/use-online-status';

export type CookingTimerStatus = 'RUNNING' | 'PAUSED' | 'DONE';

export interface CookingTimer {
  id: string;
  label: string;
  durationSeconds: number;
  /** Epoch ms when the timer finishes (running timers only). */
  endsAt: number | null;
  /** Remaining ms captured when paused. */
  remainingMs: number;
  status: CookingTimerStatus;
  recipeId: string | null;
  stepId: string | null;
  /** Id of the mirrored server timer, when the API accepted it. */
  serverId: string | null;
  createdAt: number;
  /** Set once the user dismissed the "done" state. */
  acknowledged: boolean;
}

export interface StartTimerInput {
  label: string;
  durationSeconds: number;
  recipeId?: string | null;
  stepId?: string | null;
}

const STORAGE_KEY = 'rm.timers';
const TICK_MS = 500;

type Listener = () => void;
const listeners = new Set<Listener>();
let timers: CookingTimer[] = load();
let ticker: ReturnType<typeof setInterval> | null = null;
let onDoneHandlers: Array<(timer: CookingTimer) => void> = [];

function load(): CookingTimer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CookingTimer[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(timers));
  } catch {
    // ignore
  }
}

function emit() {
  persist();
  listeners.forEach((l) => l());
  ensureTicker();
}

function set(next: CookingTimer[]) {
  timers = next;
  emit();
}

export function remainingMs(timer: CookingTimer, now = Date.now()): number {
  if (timer.status === 'RUNNING' && timer.endsAt !== null) return Math.max(0, timer.endsAt - now);
  if (timer.status === 'DONE') return 0;
  return timer.remainingMs;
}

function tick() {
  const now = Date.now();
  let changed = false;
  const finished: CookingTimer[] = [];
  timers = timers.map((t) => {
    if (t.status === 'RUNNING' && t.endsAt !== null && t.endsAt <= now) {
      changed = true;
      const done: CookingTimer = { ...t, status: 'DONE', endsAt: null, remainingMs: 0 };
      finished.push(done);
      return done;
    }
    return t;
  });
  if (changed) {
    emit();
    for (const t of finished) {
      onDoneHandlers.forEach((h) => h(t));
      mirror(t, { action: 'complete' });
    }
  } else {
    // Still notify subscribers so countdown displays re-render.
    listeners.forEach((l) => l());
  }
}

function ensureTicker() {
  const anyRunning = timers.some((t) => t.status === 'RUNNING');
  if (anyRunning && !ticker) ticker = setInterval(tick, TICK_MS);
  if (!anyRunning && ticker) {
    clearInterval(ticker);
    ticker = null;
  }
}

/** Best-effort mirror to the server so timers survive reloads on other devices. */
function mirror(
  timer: CookingTimer,
  action: { action: 'pause' | 'resume' | 'cancel' | 'complete' | 'add'; addSeconds?: number },
) {
  if (!timer.serverId || !isOnline() || authStore.getState().status !== 'authenticated') return;
  void api.timers.act(timer.serverId, action).catch(() => undefined);
}

async function mirrorCreate(timer: CookingTimer) {
  if (!isOnline() || authStore.getState().status !== 'authenticated') return;
  try {
    const created = await api.timers.create({
      label: timer.label,
      durationSeconds: timer.durationSeconds,
      recipeId: timer.recipeId,
      stepId: timer.stepId,
    });
    timers = timers.map((t) => (t.id === timer.id ? { ...t, serverId: created.id } : t));
    persist();
  } catch {
    // server mirroring is optional
  }
}

export const timerStore = {
  subscribe(listener: Listener) {
    listeners.add(listener);
    ensureTicker();
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot: () => timers,

  start(input: StartTimerInput): CookingTimer {
    const now = Date.now();
    const timer: CookingTimer = {
      id: crypto.randomUUID(),
      label: input.label,
      durationSeconds: input.durationSeconds,
      endsAt: now + input.durationSeconds * 1000,
      remainingMs: input.durationSeconds * 1000,
      status: 'RUNNING',
      recipeId: input.recipeId ?? null,
      stepId: input.stepId ?? null,
      serverId: null,
      createdAt: now,
      acknowledged: false,
    };
    set([...timers, timer]);
    void mirrorCreate(timer);
    return timer;
  },

  pause(id: string) {
    const now = Date.now();
    set(
      timers.map((t) =>
        t.id === id && t.status === 'RUNNING'
          ? { ...t, status: 'PAUSED', remainingMs: remainingMs(t, now), endsAt: null }
          : t,
      ),
    );
    const t = timers.find((x) => x.id === id);
    if (t) mirror(t, { action: 'pause' });
  },

  resume(id: string) {
    const now = Date.now();
    set(
      timers.map((t) =>
        t.id === id && t.status === 'PAUSED'
          ? { ...t, status: 'RUNNING', endsAt: now + t.remainingMs }
          : t,
      ),
    );
    const t = timers.find((x) => x.id === id);
    if (t) mirror(t, { action: 'resume' });
  },

  addSeconds(id: string, seconds: number) {
    const now = Date.now();
    set(
      timers.map((t) => {
        if (t.id !== id) return t;
        if (t.status === 'RUNNING' && t.endsAt !== null)
          return {
            ...t,
            endsAt: t.endsAt + seconds * 1000,
            durationSeconds: t.durationSeconds + seconds,
          };
        if (t.status === 'PAUSED')
          return {
            ...t,
            remainingMs: t.remainingMs + seconds * 1000,
            durationSeconds: t.durationSeconds + seconds,
          };
        // DONE: restart with the extra time
        return {
          ...t,
          status: 'RUNNING',
          endsAt: now + seconds * 1000,
          remainingMs: seconds * 1000,
          acknowledged: false,
        };
      }),
    );
    const t = timers.find((x) => x.id === id);
    if (t) mirror(t, { action: 'add', addSeconds: seconds });
  },

  cancel(id: string) {
    const t = timers.find((x) => x.id === id);
    set(timers.filter((x) => x.id !== id));
    if (t) mirror(t, { action: 'cancel' });
  },

  acknowledge(id: string) {
    set(timers.filter((t) => t.id !== id || t.status !== 'DONE'));
  },

  onDone(handler: (timer: CookingTimer) => void) {
    onDoneHandlers = [...onDoneHandlers, handler];
    return () => {
      onDoneHandlers = onDoneHandlers.filter((h) => h !== handler);
    };
  },

  /** Test helper. */
  reset() {
    set([]);
  },
};

export function useTimers(): CookingTimer[] {
  return useSyncExternalStore(timerStore.subscribe, timerStore.getSnapshot, timerStore.getSnapshot);
}

export function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

// ---------------------------------------------------------------------------
// Cues: notification, sound, vibration
// ---------------------------------------------------------------------------

export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

let audioContext: AudioContext | null = null;

export function playTimerSound() {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audioContext ??= new Ctx();
    const ctx = audioContext;
    const start = ctx.currentTime;
    [0, 0.35, 0.7].forEach((offset) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.4, start + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start + offset);
      osc.stop(start + offset + 0.32);
    });
  } catch {
    // audio not available
  }
}

export function timerCue(timer: CookingTimer, texts: { title: string; body: string }) {
  playTimerSound();
  try {
    navigator.vibrate?.([300, 150, 300, 150, 300]);
  } catch {
    // ignore
  }
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      const n = new Notification(texts.title, { body: texts.body, tag: `timer-${timer.id}` });
      n.onclick = () => window.focus();
    } catch {
      // Some platforms only allow notifications from a service worker.
    }
  }
}
